import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet'
import Hero from '../components/nav/Hero'
import { FiMapPin, FiUsers } from "react-icons/fi";
import { FiShield, FiCheckCircle, FiArrowRight } from "react-icons/fi";
import { Link, useLocation } from 'react-router-dom';
import FadeInSection from '../components/nav/FadeInSection';
import about from '../assets/about.png'

function AboutDifferentiators() {
    const differentiators = [
        {
            icon: <FiMapPin className="w-8 h-8 text-primary" />,
            title: "Local Expertise",
            description: "Deeply rooted in our operating regions, we understand the specific security nuances of every district we serve. Our granular knowledge ensures rapid response times and culturally sensitive engagement."
        },
        {
            icon: <FiUsers className="w-8 h-8 text-primary" />,
            title: "Fair Wages & Retention",
            description: "High-quality security starts with valued personnel. By paying above industry standards we ensure low turnover, high morale, and a team genuinely invested in your safety."
        },
        {
            icon: <FiShield className="w-8 h-8 text-primary" />,
            title: "Rigorous Vetting",
            description: "Our selection process is exhaustive. We do comprehensive background checks, and all staf undergo scenario-based testing to ensure only the most disciplined professionals wear the Guardhart uniform."
        }
    ];

    return (
        <section className="bg-base-100 border-b border-zinc-200 ">
            <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 ">
                    <div className="p-8 md:p-16 flex flex-col justify-center border-b lg:border-b-0 lg:border-r border-zinc-200">
                        <div className="space-y-12 max-w-xl mx-auto lg:mx-0">
                            {differentiators.map((item, index) => (
                                <div key={index} className="group">
                                    <div className="flex items-center gap-4 mb-4">
                                        {item.icon}
                                        <h3 className="text-xl md:text-2xl font-bold text-base-content">
                                            {item.title}
                                        </h3>
                                    </div>
                                    <p className="text-zinc-600 leading-relaxed">
                                        {item.description}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="relative h-[400px] lg:h-auto bg-base-200 overflow-hidden group">
                        <div
                            className="w-full h-full bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                            style={{
                                backgroundImage: `url(${about})`,
                            }}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
}

function AboutSection() {
    return (
        <section className="w-full py-16 bg-base-100 border-b border-zinc-200" id="about">
            <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
                <div className="flex justify-center mb-8">
                    <div className="p-4 rounded-full bg-base-200/50 border border-zinc-200">
                        <FiShield className="w-8 h-8 text-primary" />
                    </div>
                </div>

                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-base-content mb-8">
                    About Guardhart
                </h2>
                <div className="space-y-6 text-base text-zinc-600 leading-relaxed max-w-3xl mx-auto">
                    <p>
                        Founded in 2022, Guardhart Security Services is a Kenyan-owned company providing premium physical security and technology-enabled surveillance solutions across East Africa.
                    </p>
                    <p>
                        With headquarters in Nairobi and six branches nationwide, we serve corporates, schools, hotels, NGOs, and high-net-worth individuals. Our team of 100+ guards and 15 supervisors delivers six specialized service lines, from armed protection to smart CCTV integration.
                    </p>
                    <p>
                        We are PSRA licensed and every staff member is fuly ensured. We provide continuous training beyond regulatory requirements.
                    </p>
                </div>

                <div className="flex items-center justify-center gap-4 mt-12">
                    <div className="h-px w-16 bg-zinc-800"></div>
                    <div className="w-2 h-2 rounded-full bg-primary"></div>
                    <div className="h-px w-16 bg-zinc-800"></div>
                </div>

                <p className="mt-8 text-sm font-bold uppercase tracking-widest text-primary">
                    Protecting Kenya's Future Today
                </p>

            </div>
        </section>
    );
}

function AboutCTA() {
    return (
        <section data-theme="guardhartDark" className="relative py-24 bg-base-200 border-b border-zinc-800 overflow-hidden" id="milestones">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

                    <div className="text-center lg:text-left">
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-base-content mb-6">
                            Partner with Guardhart
                        </h2>
                        <p className="text-base text-zinc-400 mb-8 max-w-lg mx-auto lg:mx-0 leading-relaxed">
                            Your security, our commitment. Let's discuss how our certified, locally-rooted team can protect your assets, people, and peace of mind.
                        </p>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8 max-w-lg mx-auto lg:mx-0">
                            <div className="flex items-center gap-3 text-left">
                                <FiCheckCircle className="w-5 h-5 text-primary shrink-0" />
                                <span className="text-sm text-zinc-300">Kenyan-owned expertise</span>
                            </div>
                            <div className="flex items-center gap-3 text-left">
                                <FiCheckCircle className="w-5 h-5 text-primary shrink-0" />
                                <span className="text-sm text-zinc-300">24/7 rapid response</span>
                            </div>
                            <div className="flex items-center gap-3 text-left">
                                <FiCheckCircle className="w-5 h-5 text-primary shrink-0" />
                                <span className="text-sm text-zinc-300">PSRA Licensed</span>
                            </div>
                            <div className="flex items-center gap-3 text-left">
                                <FiCheckCircle className="w-5 h-5 text-primary shrink-0" />
                                <span className="text-sm text-zinc-300">Scalable protection plans</span>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                            <Link
                                to="/contact"
                                className="inline-flex items-center justify-center gap-2 bg-primary text-primary-content px-8 py-4 text-xs font-bold uppercase tracking-widest rounded active:scale-95 transition-all hover:brightness-110"
                            >
                                Get in Touch
                                <FiArrowRight className="w-4 h-4" />
                            </Link>
                            <Link
                                to="/services"
                                className="inline-flex items-center justify-center gap-2 border border-zinc-700 text-zinc-100 px-8 py-4 text-xs font-bold uppercase tracking-widest rounded active:scale-95 transition-all hover:bg-zinc-800 hover:border-zinc-600"
                            >
                                Explore Services
                            </Link>
                        </div>
                    </div>

                    <div className="flex justify-center">
                        <div className="relative">
                            <div className="absolute inset-0 bg-primary/5 rounded-full blur-3xl" />
                            <div className="relative size-64 rounded-full border-[16px] border-zinc-200 flex items-center justify-center">
                                <FiShield className="text-[100px] text-primary/20" />
                            </div>
                            <div className="absolute -top-4 -right-4 bg-base-100 border border-zinc-200 rounded-lg px-4 py-2 shadow-lg">
                                <span className="text-xs font-bold text-primary">100+ Guards</span>
                            </div>
                            <div className="absolute -bottom-4 -left-4 bg-base-100 border border-zinc-200 rounded-lg px-4 py-2 shadow-lg">
                                <span className="text-xs font-bold text-primary">6 Branches</span>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
}

function AboutPage(props) {
    const url = window.location.href
    const location = useLocation();
    useEffect(() => {
        const hash = location.hash;
        if (hash) {
            const element = document.querySelector(hash);
            if (element) {
                setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth' });
                }, 54);
            }
        }
    }, [location.hash]);
    return (
        <div>
            <Helmet>
                <title>About Guardhart Security </title>
                <link rel="canonical" href={url} />
                <meta name="description" content="Learn about Guardhart Security Services - a Kenyan-owned security company founded in 2022. ISO 18788 certified, PSRA licensed, with 100+ professionally trained guards across 6 branches nationwide." />

                <meta property="og:type" content="website" />
                <meta property="og:url" content={url} />
                <meta property="og:title" content="About Guardhart Security | Kenyan-Owned, ISO 18788 Certified" />
                <meta property="og:description" content="A Kenyan-owned security company founded in 2022. ISO 18788 certified, PSRA licensed, with 100+ professionally trained guards across 6 branches nationwide." />

                <meta property="twitter:card" content="summary_large_image" />
                <meta property="twitter:title" content="About Guardhart Security | Kenyan-Owned, ISO 18788 Certified" />
                <meta property="twitter:description" content="A Kenyan-owned security company founded in 2022. ISO 18788 certified, PSRA licensed, with 100+ professionally trained guards across 6 branches nationwide." />
            </Helmet>
            <Hero
                label="About Guardhart"
                title="Kenyan-Owned. Globally Certified."
                description="Founded in 2022, we are a Kenyan-owned security company providing premium protection and tech solutions. We treat our guards well, and they treat your security seriously."
                primaryBtnText="Our Story"
                primaryBtnLink="#about"
                showSecondaryBadge={false}
            />
            <FadeInSection>
                <AboutSection />
            </FadeInSection>
            <FadeInSection>
                <AboutDifferentiators />
            </FadeInSection>
            <FadeInSection>
                <AboutCTA />
            </FadeInSection>

        </div>
    )
}

export default AboutPage